import React, { useState } from 'react';
import axios from 'axios';
import { Fish, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SeafoodProcessingContact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    facility_location: '',
    facility_type: '',
    processing_capacity: '',
    seasonal_operation: '',
    fish_drying: '',
    power_generation: '',
    power_capacity: '',
    fuel_type: '',
    voc_sources: '',
    permit_status: '',
    permit_needs: '',
    timeline: '',
    specific_requirements: '',
    additional_info: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        subject: `Seafood Processing Inquiry - ${formData.facility_type}`,
        message: `
Company: ${formData.company}
Phone: ${formData.phone}
Facility Location: ${formData.facility_location}
Facility Type: ${formData.facility_type}
Processing Capacity: ${formData.processing_capacity}
Seasonal Operation: ${formData.seasonal_operation}
Fish Drying Operations: ${formData.fish_drying}
Power Generation: ${formData.power_generation}
Power Capacity: ${formData.power_capacity}
Fuel Type: ${formData.fuel_type}
VOC Sources: ${formData.voc_sources}
Permit Status: ${formData.permit_status}
Permit Needs: ${formData.permit_needs}
Timeline: ${formData.timeline}
Specific Requirements or Concerns: ${formData.specific_requirements}
Additional Information: ${formData.additional_info}
        `.trim(),
        newsletter_signup: false,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your seafood processing facility details and contact you within 1-2 business days.',
        });
        setFormData({
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          facility_location: '',
          facility_type: '',
          processing_capacity: '',
          seasonal_operation: '',
          fish_drying: '',
          power_generation: '',
          power_capacity: '',
          fuel_type: '',
          voc_sources: '',
          permit_status: '',
          permit_needs: '',
          timeline: '',
          specific_requirements: '',
          additional_info: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-900 to-cyan-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Fish className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Seafood Processing Facility Inquiry</h1>
                <p className="text-cyan-100 mt-2">Air quality management for seafood processing operations</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company <span className="text-red-500">*</span></label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Facility Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Location</label>
                    <input type="text" name="facility_location" value={formData.facility_location} onChange={handleChange} placeholder="City/Region in Alaska" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                    <select name="facility_type" value={formData.facility_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select facility type</option>
                      <option value="fish_processing">Fish Processing Plant</option>
                      <option value="crab_processing">Crab Processing</option>
                      <option value="salmon_processing">Salmon Processing</option>
                      <option value="multiple_species">Multiple Species Processing</option>
                      <option value="freezing_storage">Freezing & Storage</option>
                      <option value="canning">Canning Operations</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Processing Capacity</label>
                    <input type="text" name="processing_capacity" value={formData.processing_capacity} onChange={handleChange} placeholder="e.g., 100,000 lbs/day, 50 tons/day" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Seasonal Operation</label>
                    <select name="seasonal_operation" value={formData.seasonal_operation} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select operation type</option>
                      <option value="year_round">Year-Round Operation</option>
                      <option value="summer_only">Summer Season Only</option>
                      <option value="specific_seasons">Specific Fishing Seasons</option>
                      <option value="intermittent">Intermittent Operation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fish Drying Operations</label>
                    <select name="fish_drying" value={formData.fish_drying} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select option</option>
                      <option value="yes_major">Yes - Major Drying Operations</option>
                      <option value="yes_minor">Yes - Minor Drying Operations</option>
                      <option value="smoking">Smoking/Curing Operations</option>
                      <option value="no">No Drying Operations</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Power Generation On-Site</label>
                    <select name="power_generation" value={formData.power_generation} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select option</option>
                      <option value="yes_primary">Yes - Primary Power Source</option>
                      <option value="yes_backup">Yes - Backup/Emergency Only</option>
                      <option value="no_grid">No - Grid Power</option>
                    </select>
                  </div>

                  {(formData.power_generation === 'yes_primary' || formData.power_generation === 'yes_backup') && (
                    <>
                      <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Power Generation Capacity</label>
                        <input type="text" name="power_capacity" value={formData.power_capacity} onChange={handleChange} placeholder="e.g., 500 kW, 1 MW" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                      </div>

                      <div className="bg-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Type</label>
                        <select name="fuel_type" value={formData.fuel_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                          <option value="">Select fuel type</option>
                          <option value="diesel">Diesel</option>
                          <option value="natural_gas">Natural Gas</option>
                          <option value="dual_fuel">Dual Fuel</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VOC Emission Sources</label>
                    <select name="voc_sources" value={formData.voc_sources} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select primary source</option>
                      <option value="fish_drying">Fish Drying/Smoking</option>
                      <option value="cooking">Cooking Operations</option>
                      <option value="rendering">Rendering/By-product Processing</option>
                      <option value="cleaning">Cleaning/Sanitation</option>
                      <option value="multiple">Multiple VOC Sources</option>
                      <option value="minimal">Minimal VOC Concerns</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Permit Status</label>
                    <select name="permit_status" value={formData.permit_status} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select status</option>
                      <option value="no_permit">No Permit (New Facility)</option>
                      <option value="planning">Planning Phase</option>
                      <option value="existing">Have Existing Permit</option>
                      <option value="renewal">Permit Renewal Needed</option>
                      <option value="modification">Facility Modification</option>
                      <option value="compliance_issue">Compliance Issue</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permit Needs</label>
                    <select name="permit_needs" value={formData.permit_needs} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select primary need</option>
                      <option value="small_power_plant">Small Power Plant Permit (Under 500 TPY)</option>
                      <option value="title_v">Title V Operating Permit</option>
                      <option value="nsps_compliance">NSPS Compliance (Subpart IIII, JJJJ)</option>
                      <option value="neshap_compliance">NESHAP Compliance (Subpart ZZZZ)</option>
                      <option value="voc_control">VOC Emission Control</option>
                      <option value="odor_control">Odor Control Plan</option>
                      <option value="comprehensive">Comprehensive Permitting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select timeline</option>
                      <option value="before_season">Before Fishing Season</option>
                      <option value="immediate">Immediate (Within 2 weeks)</option>
                      <option value="1_month">Within 1 month</option>
                      <option value="1-3_months">1-3 months</option>
                      <option value="3-6_months">3-6 months</option>
                      <option value="planning">Long-term Planning</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specific_requirements" value={formData.specific_requirements} onChange={handleChange} required rows="4" placeholder="Worker health, particulate matter, odor complaints, diesel generator permits, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows="3" placeholder="Any other details about your facility..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-cyan-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-cyan-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Seafood Processing Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeafoodProcessingContact;
